import * as THREE from 'three';
import { DEBUG, DETECTION, HUD } from '../config/tuning';
import { bus } from '../core/EventBus';
import { SiteState } from '../threat/AirDefenceSite';
import type { ThreatField } from '../threat/ThreatField';
import type { Terrain } from '../world/Terrain';

/**
 * The threat debug visualisation (an M5 implementation note, and not optional).
 *
 * The notch mechanic is invisible without this. A player flying perpendicular to a radar sees a
 * tick on the RWR change shape; a developer tuning the notch needs to see *which of the four
 * tests* is failing, by how much, and how close the radial velocity is to the threshold — none of
 * which can be inferred from the outside. Tuning it blind is not possible, so the instrument is
 * built alongside the mechanic rather than after it.
 *
 * Two halves:
 *  - a DOM panel, one row per site, showing state, the four test results and the timers;
 *  - in-world geometry: search and engagement rings laid on the terrain, and the line-of-sight
 *    ray to the aircraft, drawn green when it is clear and red where it is blocked.
 */

const PANEL_STYLE = `
  position: fixed; bottom: 12px; left: 12px; z-index: 20;
  font: 11px/1.5 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  letter-spacing: 0.03em; text-transform: uppercase; white-space: pre;
  color: ${HUD.colour}; background: rgba(4, 10, 12, 0.78);
  border: 1px solid rgba(63, 232, 216, 0.35); border-radius: 2px;
  padding: 8px 10px; pointer-events: none;
`;

/** Ring segments. Enough that a 28 km circle does not read as a polygon. */
const RING_SEGMENTS = 128;
/** Metres above the terrain the rings float, so they do not z-fight with it. */
const RING_LIFT_M = 6;

const COLOUR_SEARCH = 0x3fe8d8;
const COLOUR_ENGAGE = 0xffb000;
const COLOUR_CLEAR = 0x54ff9f;
const COLOUR_BLOCKED = 0xff3b30;

const losScratch = new THREE.Vector3();

export class ThreatDebug {
  readonly object3d = new THREE.Group();

  private readonly field: ThreatField;
  private readonly terrain: Terrain;
  private readonly root: HTMLDivElement;
  private readonly rows: HTMLDivElement[] = [];
  private readonly rings: THREE.Line[] = [];
  private readonly losLines: THREE.LineSegments;
  private builtSiteCount = -1;
  private builtJammerCount = -1;

  /** One two-vertex line per site: radar to aircraft. */
  private losGeometry: THREE.BufferGeometry = new THREE.BufferGeometry();
  private losPositions: Float32Array = new Float32Array(0);
  private losColours: Float32Array = new Float32Array(0);

  private visible = false;
  private lastTextUpdateMs = 0;

  constructor(field: ThreatField, terrain: Terrain, parent: HTMLElement = document.body) {
    this.field = field;
    this.terrain = terrain;

    // --- Panel
    this.root = document.createElement('div');
    this.root.setAttribute('style', PANEL_STYLE);
    this.root.style.display = 'none';

    const title = document.createElement('div');
    title.textContent = 'THREATS · §3.5   SITE  STATE      HOR LOS RNG DOP   RANGE   VR    T';
    title.style.opacity = '0.6';
    title.style.marginBottom = '3px';
    this.root.appendChild(title);

    parent.appendChild(this.root);

    const losMaterial = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      depthTest: false,
    });
    this.losLines = new THREE.LineSegments(new THREE.BufferGeometry(), losMaterial);
    this.losLines.frustumCulled = false;
    this.losLines.renderOrder = 900;
    this.object3d.add(this.losLines);

    this.rebuild();

    this.object3d.visible = false;
    bus.on('debug:visibility', ({ visible }) => this.setVisible(visible));
  }

  /**
   * Build the per-site rows, rings and ray buffers for the field as it stands now.
   *
   * Originally this happened once in the constructor, which was correct while the theatre was a
   * fixed demo laydown. Missions changed that: picking one from the shell replaces the whole site
   * list, so a panel built against the previous mission indexes past the end of its own row array
   * and throws once per frame, forever. Rebuilding on a count change is the fix — the panel has to
   * follow the world rather than assume it.
   */
  private rebuild(): void {
    const field = this.field;

    for (let i = 0; i < this.rows.length; i += 1) this.rows[i].remove();
    this.rows.length = 0;
    for (let i = this.rings.length - 1; i >= 0; i -= 1) {
      this.object3d.remove(this.rings[i]);
      this.rings[i].geometry.dispose();
    }
    this.rings.length = 0;

    for (let i = 0; i < field.sites.length; i += 1) {
      const row = document.createElement('div');
      this.rows.push(row);
      this.root.appendChild(row);
    }

    const jammerLine = document.createElement('div');
    jammerLine.style.opacity = '0.75';
    jammerLine.style.marginTop = '3px';
    this.rows.push(jammerLine);
    this.root.appendChild(jammerLine);

    // --- Rings, one pair per site, laid on the terrain.
    for (let i = 0; i < field.sites.length; i += 1) {
      const site = field.sites[i];
      this.addRing(this.buildRing(site.positionM, site.params.searchRangeM, COLOUR_SEARCH, 0.35));
      this.addRing(this.buildRing(site.positionM, site.params.maxEngageM, COLOUR_ENGAGE, 0.75));
    }
    for (let i = 0; i < field.ew.jammers.length; i += 1) {
      const jammer = field.ew.jammers[i];
      this.addRing(this.buildRing(jammer.positionM, jammer.radiusM, 0xb46cff, 0.4));
    }

    // --- Line-of-sight rays, rebuilt every frame; the buffers are sized to the site count.
    this.losPositions = new Float32Array(field.sites.length * 6);
    this.losColours = new Float32Array(field.sites.length * 6);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.losPositions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(this.losColours, 3));
    this.losGeometry.dispose();
    this.losGeometry = geometry;
    this.losLines.geometry = geometry;

    this.builtSiteCount = field.sites.length;
    this.builtJammerCount = field.ew.jammers.length;
  }

  private addRing(ring: THREE.Line): void {
    this.rings.push(ring);
    this.object3d.add(ring);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.object3d.visible = visible;
    this.root.style.display = visible ? 'block' : 'none';
  }

  /** Refresh the rays every frame and the text at the overlay's throttled rate. */
  update(aircraftPositionM: THREE.Vector3): void {
    if (!this.visible) return;

    // A mission change replaces the whole site list, so the panel follows it rather than
    // assuming the theatre it was constructed against is still the one being flown.
    if (
      this.field.sites.length !== this.builtSiteCount ||
      this.field.ew.jammers.length !== this.builtJammerCount
    ) {
      this.rebuild();
    }

    this.updateRays(aircraftPositionM);

    const nowMs = performance.now();
    if (nowMs - this.lastTextUpdateMs < 1000 / DEBUG.TEXT_UPDATE_HZ) return;
    this.lastTextUpdateMs = nowMs;
    this.updateText();
  }

  dispose(): void {
    this.root.remove();
    this.losGeometry.dispose();
  }

  private updateRays(aircraftPositionM: THREE.Vector3): void {
    const sites = this.field.sites;
    for (let i = 0; i < sites.length; i += 1) {
      const site = sites[i];
      const base = i * 6;

      this.losPositions[base] = site.radarPositionM.x;
      this.losPositions[base + 1] = site.radarPositionM.y;
      this.losPositions[base + 2] = site.radarPositionM.z;

      // A blocked ray stops where the terrain cuts it, which shows *where* the mask is rather
      // than merely that one exists.
      const blockedAtM = site.tests.hasLineOfSight
        ? -1
        : this.terrain.firstOcclusionM(site.radarPositionM, aircraftPositionM);

      if (blockedAtM < 0) {
        losScratch.copy(aircraftPositionM);
      } else {
        losScratch
          .copy(aircraftPositionM)
          .sub(site.radarPositionM)
          .normalize()
          .multiplyScalar(blockedAtM)
          .add(site.radarPositionM);
      }

      this.losPositions[base + 3] = losScratch.x;
      this.losPositions[base + 4] = losScratch.y;
      this.losPositions[base + 5] = losScratch.z;

      const colour = site.tests.hasLineOfSight ? COLOUR_CLEAR : COLOUR_BLOCKED;
      const r = ((colour >> 16) & 0xff) / 255;
      const g = ((colour >> 8) & 0xff) / 255;
      const b = (colour & 0xff) / 255;
      for (let v = 0; v < 2; v += 1) {
        this.losColours[base + v * 3] = r;
        this.losColours[base + v * 3 + 1] = g;
        this.losColours[base + v * 3 + 2] = b;
      }
    }

    this.losGeometry.attributes['position'].needsUpdate = true;
    this.losGeometry.attributes['color'].needsUpdate = true;
    this.losGeometry.setDrawRange(0, sites.length * 2);
  }

  private updateText(): void {
    const sites = this.field.sites;
    for (let i = 0; i < sites.length; i += 1) {
      const site = sites[i];
      const tests = site.tests;

      // The timer that currently matters: whichever one the state is counting on.
      let timer = '';
      if (site.state === SiteState.Searching) {
        timer = `A ${site.acquireHoldS.toFixed(1)}/${site.effectiveAcquireS.toFixed(1)}`;
      } else if (site.state === SiteState.Tracking) {
        timer =
          site.reactionHoldS > 0
            ? `R ${site.reactionHoldS.toFixed(1)}/${site.effectiveReactionS.toFixed(1)}`
            : `M ${site.lockLostS.toFixed(1)}/${site.params.trackMemoryS.toFixed(1)}`;
      } else if (site.state === SiteState.Guiding) {
        timer = `${site.magazine} LEFT`;
      }

      // The notch margin is the number that matters most: how far the radial velocity is from the
      // threshold, signed, so it is obvious which way to turn.
      const notchMarginMs = Math.abs(tests.radialVelocityMs) - site.params.notchVelocityMs;

      this.rows[i].textContent =
        `${site.designation.padEnd(4)} ${site.state.toUpperCase().padEnd(10)} ` +
        `${mark(tests.withinHorizon)}   ${mark(tests.hasLineOfSight)}   ${mark(tests.withinRange)}   ` +
        `${tests.dopplerVisibility.toFixed(2)}  ` +
        `${(tests.rangeM / 1000).toFixed(1)}K  ` +
        `${notchMarginMs >= 0 ? '+' : ''}${notchMarginMs.toFixed(0)}  ${timer}`;
      this.rows[i].style.color = site.hasTrack ? HUD.dangerColour : HUD.colour;
    }

    const ew = this.field.ew;
    this.rows[sites.length].textContent =
      `EW   JAM ${ew.jamming.toFixed(2)}  GPS ERR ${ew.gpsErrorM.length().toFixed(0)}M` +
      `${ew.linkLost ? '  *** LINK LOST ***' : ''}  ` +
      `NOTCH ${DETECTION.notchVelocityMs}±${DETECTION.notchSoftnessMs} M/S`;
  }

  /** A circle of the given radius, following the terrain so it reads as a ring on the ground. */
  private buildRing(
    centreM: THREE.Vector3,
    radiusM: number,
    colour: number,
    opacity: number,
  ): THREE.Line {
    const positions = new Float32Array((RING_SEGMENTS + 1) * 3);
    for (let i = 0; i <= RING_SEGMENTS; i += 1) {
      const angle = (i / RING_SEGMENTS) * Math.PI * 2;
      const x = centreM.x + Math.cos(angle) * radiusM;
      const z = centreM.z + Math.sin(angle) * radiusM;
      positions[i * 3] = x;
      positions[i * 3 + 1] = this.terrain.surfaceHeightAt(x, z) + RING_LIFT_M;
      positions[i * 3 + 2] = z;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const material = new THREE.LineBasicMaterial({ color: colour, transparent: true, opacity });
    const line = new THREE.Line(geometry, material);
    line.frustumCulled = false;
    return line;
  }
}

function mark(passed: boolean): string {
  return passed ? '+' : '·';
}
